import { t } from '@hefe/i18n';
import { NotificationType } from '@hefe/models';
import {
  currentUserSelector,
  fetchUserNotificationTypes,
  notificationTypesSelector,
  updateUserNotifications,
} from '@hefe/store';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Col,
  Row,
  Spin,
  Table,
  Tabs,
  Typography,
} from 'antd';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';

export default function UserSettingsNotificationsComponent() {
  const dispatch = useAppDispatch();
  const notificationTypes = useSelector(notificationTypesSelector);
  const user = useSelector(currentUserSelector);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({});

  useEffect(() => {
    fetchNotificationTypes();
  }, []);

  useEffect(() => {
    if (user?.user_settings.notifications) {
      setNotificationSettings(user?.user_settings.notifications);
    }
  }, [user?.user_settings.notifications]);

  const fetchNotificationTypes = () => {
    setLoading(true);
    dispatch(
      fetchUserNotificationTypes({
        cacheDuration: 60 * 60 * 1000,
      })
    )
      .unwrap()
      .finally(() => setLoading(false));
  };

  const saveNotifications = () => {
    setSaving(true);

    dispatch(updateUserNotifications(notificationSettings))
      .unwrap()
      .finally(() => setSaving(false));
  };

  const setCheckboxChecked = (
    checked: boolean,
    item: NotificationType,
    type: 'mail' | 'push'
  ) => {
    const key = `${item.key}.${type}`;
    const settings = _.cloneDeep(notificationSettings);

    if (item.children) {
      for (const child of item.children) {
        setCheckboxChecked(checked, child, type);
      }
    } else {
      _.set(settings, key, checked);
    }

    setNotificationSettings((oldStateValue) => (oldStateValue = settings));
  };

  const renderTopCategories = () => {
    const tabs = [];

    if (!notificationTypes) {
      return;
    }

    for (const type of notificationTypes) {
      if (type.children) {
        tabs.push(
          <Tabs.TabPane key={type.key} tab={t(`notifications.${type.key}`)}>
            <Table
              pagination={false}
              dataSource={type.children}
              expandable={{ defaultExpandAllRows: true }}
              columns={[
                {
                  dataIndex: 'key',
                  title: t('name'),
                  render: (value: string, item: NotificationType) => {
                    const keyArray = value.split('.');
                    const key = keyArray[keyArray.length - 1];
                    return (
                      <Badge dot={item.new_category}>
                        <Typography.Text strong={!!item.children}>
                          {t(`notifications.${key}`)}
                        </Typography.Text>
                      </Badge>
                    );
                  },
                },
                {
                  title: t('email'),
                  render: (value: string, item: NotificationType) => {
                    if (item.children) {
                      return;
                    }
                    const key = `${item.key}.mail`;
                    const checked = _.get(notificationSettings, key);
                    return (
                      <Checkbox
                        checked={checked}
                        onChange={(event) =>
                          setCheckboxChecked(event.target.checked, item, 'mail')
                        }
                      />
                    );
                  },
                },
                {
                  title: t('push'),
                  render: (value: string, item: NotificationType) => {
                    if (item.children) {
                      return;
                    }
                    const key = `${item.key}.push`;
                    const checked = _.get(notificationSettings, key);
                    return (
                      <Checkbox
                        checked={checked}
                        onChange={(event) =>
                          setCheckboxChecked(event.target.checked, item, 'push')
                        }
                      />
                    );
                  },
                },
              ]}
            />
          </Tabs.TabPane>
        );
      }
    }

    return tabs;
  };

  if (loading) {
    return (
      <Row justify="center">
        <Spin size="large" />
      </Row>
    );
  }

  if (!user) return <div></div>;

  return (
    <Row justify="center">
      <Col xs={24} md={12}>
        {user.user_settings.notifications !== notificationSettings && (
          <Alert
            message={t('information_texts.save_reminder_bottom')}
            type="warning"
          />
        )}
        <Tabs>{renderTopCategories()}</Tabs>
        <Button
          style={{ marginTop: 20 }}
          onClick={() => saveNotifications()}
          disabled={user.user_settings.notifications === notificationSettings}
          loading={saving}
        >
          {t('save')}
        </Button>
      </Col>
    </Row>
  );
}
